import { Injectable } from '@angular/core';
import { ApiService, AppSettingsService, LocalStorageService, ShopperService } from '@woolworthsnz/styleguide';
import {
	MealPlanPreferences,
	MealPlanType,
	MealShortlist,
	MealShortlistItemModel,
	RecipeStamp,
} from '@woolworthsnz/trader-api';
import { catchError, forkJoin, map, Observable, of, switchMap, zip } from 'rxjs';
import { MealPlanPreferenceName } from '../components/choose-meal-plan/choose-meal-plan.model';
import { mapToRecipeStampLocal } from '../helpers/stamp.helper';
import { MealPlanningDataLocalStorage, SelectedMealLocalStorage } from './meal-planning-data.model';
import { RecipeService } from './recipe.service';
import { tap } from 'rxjs/operators';
import { MealPlanningData, MealPlanPreference, RecipeStampLocal, Stamp } from '../models';

export const DEFAULT_MEAL_PLANNING_DATA: MealPlanningData = {
	preferences: [],
	selectedMeals: [],
};

@Injectable({ providedIn: 'root' })
export class MealPlanningService {
	constructor(
		private localStorageService: LocalStorageService,
		private shopperService: ShopperService,
		private apiService: ApiService,
		private appSettingsService: AppSettingsService,
		private recipeService: RecipeService
	) {}

	/**
	 * Fetch all active meal plan types
	 */
	public fetchMealPlanTypes(): Observable<MealPlanType[]> {
		return this.apiService.get(`${this.appSettingsService.getEndpoint('mealPlanTypes')}`, {
			params: { status: 'Active' },
		});
	}

	/**
	 * Fetch all curated recipes by meal plan types.
	 * @param mealPlanTypes Filter on meal plan types.  The default is to return recipes for all meal plan types.
	 * @param scheduleDate The default date is today, but you can override it by specifying a specific date
	 */
	public fetchCuratedRecipes(mealPlanTypes?: number[] | string[], scheduleDate?: Date): Observable<RecipeStampLocal[]> {
		const args: any =
			scheduleDate || mealPlanTypes
				? {
						params: {
							...(mealPlanTypes && { mealPlanTypes: mealPlanTypes.join(',') }),
							...(scheduleDate && { date: scheduleDate }),
						},
				  }
				: undefined;

		return this.apiService
			.get(`${this.appSettingsService.getEndpoint('mealPlanCuratedRecipes')}`, args)
			.pipe(map((recipeStamps: RecipeStamp[]): RecipeStampLocal[] => recipeStamps.map(mapToRecipeStampLocal)));
	}

	/**
	 * Load all meal planning data, which includes meal plan preferences & shortlisted meals
	 * If the user is logged in, fetch data from the server.
	 * If the user is not logged in, load the data from local storage.
	 * If the user is logged in, then work out where the data needs to be synced to
	 */
	public loadMealPlanningData(): Observable<MealPlanningData> {
		const partialLocalMealPlanningData: MealPlanningDataLocalStorage =
			this.readPartialMealPlanningDataFromLocalStorage();
		const localMealPlanningData$: Observable<MealPlanningData> =
			this.fetchMissingRecipeStamps(partialLocalMealPlanningData);

		return zip(this.isLoggedIn()).pipe(
			switchMap(([isLoggedIn]) =>
				!isLoggedIn
					? localMealPlanningData$
					: this.fetchAndSyncMealPlanningData(partialLocalMealPlanningData).pipe(
							switchMap(this.fetchMissingRecipeStamps.bind(this))
					  )
			),
			tap((newMealPlanningData) => this.writeMealPlanningDataToLocalStorage(newMealPlanningData))
		);
	}

	/**
	 * Save both meal plan preferences and meals shortlist
	 * @param mealPlanningData
	 */
	public persistMealPlanningData(mealPlanningData: MealPlanningData): Observable<MealPlanningData> {
		// Save to local storage
		this.writeMealPlanningDataToLocalStorage(mealPlanningData);

		return this.isLoggedIn().pipe(
			switchMap((isLoggedIn) =>
				!isLoggedIn
					? of(mealPlanningData)
					: // First we patch the meal plan preferences
					  forkJoin([
							this.patchPreferences(this.mapToMealPlanPreferences(mealPlanningData.preferences)),
							mealPlanningData.selectedMeals.length
								? this.replaceMealsShortlist(this.mapToMealsShortlistItems(mealPlanningData))
								: of(undefined),
					  ]).pipe(
							map(() => mealPlanningData),
							catchError(() => of(mealPlanningData))
					  )
			),
			catchError(() => of(mealPlanningData))
		);
	}

	public persistShortlistedMeal(changedMeal: Stamp, mealShortlist: Stamp[]): Observable<Stamp[]> {
		return this.isLoggedIn().pipe(
			switchMap((isLoggedIn) => {
				const addMeal = !mealShortlist.find((mealStamp) => mealStamp.recipe.id === changedMeal.recipe.id);

				const updatedShortlist = addMeal
					? [...mealShortlist, changedMeal]
					: mealShortlist
							.map((meal) => (meal.recipe.id === changedMeal.recipe.id ? changedMeal : meal))
							.filter((meal) => !!(meal.mealPlan && meal.mealPlan.addedServings > 0));

				this.writeMealsShortlistToLocalStorage(updatedShortlist);

				if (!isLoggedIn) {
					return of(updatedShortlist);
				}

				const changedSelectedMeal = {
					recipeId: changedMeal.recipe.id,
					servings: changedMeal.mealPlan?.addedServings || 0,
				};
				const result = addMeal
					? this.addShortlistMeal(changedSelectedMeal)
					: this.updateOrRemoveShortlistMeal(changedSelectedMeal);

				return result.pipe(
					map(() => updatedShortlist),
					catchError(() => of(mealShortlist))
				);
			})
		);
	}

	/**
	 * Save the meal planning preferences.
	 * - If the user is not logged in, then only save locally.
	 * - If the user is logged in, then save it both on the server and locally
	 */
	public persistMealPlanPreferences(preferences: MealPlanPreference[]): Observable<void | MealPlanPreference[]> {
		return this.isLoggedIn().pipe(
			switchMap((isLoggedIn) =>
				!isLoggedIn
					? of(this.writePreferencesToLocalStorage(preferences))
					: this.patchPreferences(this.mapToMealPlanPreferences(preferences))
			)
		);
	}

	public hasCompletedPreferences(preferences: MealPlanPreference[]): boolean {
		const preferenceNames = Object.values(MealPlanPreferenceName);
		const completedPreferences = preferences?.filter((preference) =>
			preferenceNames.includes(preference.name as unknown as MealPlanPreferenceName)
		);

		return completedPreferences?.length === preferenceNames.length;
	}

	public clearLocalMealPlanShortlist(): void {
		this.writeMealsShortlistToLocalStorage([]);
	}

	private isLoggedIn(): Observable<boolean> {
		return this.shopperService.select('isLoggedIn').pipe(map(Boolean));
	}

	private mapToMealPlanPreferences(preferences: MealPlanPreference[]): MealPlanPreferences {
		const household = preferences.find((preference) => preference.name === MealPlanPreferenceName.people);
		const mealPlanTypes = (
			preferences.find((preference) => preference.name === MealPlanPreferenceName.dietaryNeeds)?.value as string[]
		)?.map((requirement) => Number(requirement));

		return {
			household: {
				adults: (household?.value as { [key: string]: number }).adult,
				children: (household?.value as { [key: string]: number }).child,
			},
			mealPlanTypes,
		};
	}

	private readPartialMealPlanningDataFromLocalStorage(): MealPlanningDataLocalStorage {
		return this.localStorageService.getItem('mealPlanning') ?? DEFAULT_MEAL_PLANNING_DATA;
	}

	private writePreferencesToLocalStorage(preferences: MealPlanPreference[]): MealPlanPreference[] {
		const mealPlanningData = { ...this.readPartialMealPlanningDataFromLocalStorage(), preferences };
		this.localStorageService.setItem('mealPlanning', JSON.stringify(mealPlanningData));
		return preferences;
	}

	private writeMealsShortlistToLocalStorage(selectedMeals: Stamp[]): MealPlanningData {
		const mealPlanningData = { ...this.readPartialMealPlanningDataFromLocalStorage(), selectedMeals };
		return this.writeMealPlanningDataToLocalStorage(mealPlanningData);
	}

	private writeMealPlanningDataToLocalStorage(mealPlanningData: MealPlanningData): MealPlanningData {
		const localMealPlanningData = {
			...mealPlanningData,
			selectedMeals: this.mapToMealsShortlistItemsLocal(mealPlanningData.selectedMeals),
		};
		this.localStorageService.setItem('mealPlanning', JSON.stringify(localMealPlanningData));
		return mealPlanningData;
	}

	private getShortlistedMeals(): Observable<Stamp[]> {
		return this.fetchMealsShortlist().pipe(map(this.mapToUsableShortlist.bind(this)));
	}

	/**
	 * Add the meal to the logged in user's meals shortlist on the server
	 * @param meal
	 */
	private addShortlistMeal(meal: MealShortlistItemModel): Observable<void> {
		return this.apiService.post(`${this.appSettingsService.getEndpoint('mealPlanShortlist')}`, meal);
	}

	private updateOrRemoveShortlistMeal(meal: MealShortlistItemModel): Observable<void> {
		if (meal.servings === 0) {
			return this.removeShortlistMeal(meal.recipeId);
		}
		return this.updateShortlistMeal(meal);
	}

	/**
	 * This will remove recipe stamp info from the selected meals before saving it to local storage
	 * @param selectedMeals
	 * @private
	 */
	private mapToMealsShortlistItemsLocal(selectedMeals: Stamp[]): SelectedMealLocalStorage[] {
		return selectedMeals.map((meal) => ({
			id: meal.recipe.id,
			servings: Number(meal.mealPlan?.addedServings),
		}));
	}

	/**
	 * Save the meal plan preferences on the server as well as in local storage
	 * @param mealPlanPreferences
	 */
	private patchPreferences(mealPlanPreferences: MealPlanPreferences): Observable<void> {
		return this.apiService
			.patch(`${this.appSettingsService.getEndpoint('mealPlanPreferences')}`, mealPlanPreferences)
			.pipe(
				tap(() => {
					this.writePreferencesToLocalStorage(this.mapServerPreferencesToLocal(mealPlanPreferences));
				})
			);
	}

	/**
	 * This will load the meal plan preferences and meals shortlist from the server and map it to Meal Planning Data.
	 * @private
	 */
	private fetchMealPlanningData(): Observable<MealPlanningData> {
		return forkJoin([this.fetchPreferences(), this.getShortlistedMeals()]).pipe(
			map(([mealPlanPreferences, mealsShortlist]) => {
				const preferences = this.mapServerPreferencesToLocal(mealPlanPreferences);
				return {
					preferences,
					selectedMeals: mealsShortlist,
				};
			})
		);
	}

	private mapToMealsShortlistItems(mealPlanningData: MealPlanningData): MealShortlistItemModel[] {
		return mealPlanningData.selectedMeals.map((meal) => ({
			recipeId: meal.recipe.id,
			servings: meal.mealPlan?.addedServings || 0,
		}));
	}

	private fetchAndSyncMealPlanningData(partialLocalMealPlanningData: MealPlanningDataLocalStorage): Observable<{
		preferences: MealPlanPreference[];
		selectedMeals: Stamp[] | (Stamp | SelectedMealLocalStorage)[];
	}> {
		return this.fetchMealPlanningData().pipe(
			switchMap((serverMealPlanningData: MealPlanningData) => {
				// If there are no data stored locally but some were loaded from the server, the just sync local storage
				if (!partialLocalMealPlanningData.preferences.length && serverMealPlanningData.preferences.length) {
					this.writeMealPlanningDataToLocalStorage(serverMealPlanningData);
					return of(serverMealPlanningData);
				}

				// const localSelectedMealsMerged =
				// Restore recipe stamps for shortlisted meals already fetched from the server
				const hasSelectedMealsOnServer =
					partialLocalMealPlanningData?.selectedMeals.length && serverMealPlanningData.selectedMeals.length;

				const hasDifferentSelectedMealsLocally =
					hasSelectedMealsOnServer ||
					partialLocalMealPlanningData.selectedMeals.findIndex((localMeal) => {
						const shortlistedServerMeal = serverMealPlanningData.selectedMeals.find(
							(serverMeal) => serverMeal.recipe.id === localMeal.id
						);
						return !shortlistedServerMeal || shortlistedServerMeal.mealPlan?.addedServings !== localMeal.servings;
					}) !== -1;

				const mergedLocalSelectedMeals: (Stamp | SelectedMealLocalStorage)[] = !hasDifferentSelectedMealsLocally
					? partialLocalMealPlanningData.selectedMeals
					: partialLocalMealPlanningData.selectedMeals.map((meal) => {
							const matchedMeal = serverMealPlanningData.selectedMeals.find(
								(serverMeal) => serverMeal.recipe.id === meal.id
							);
							return !matchedMeal
								? meal
								: ({
										...matchedMeal,
										mealPlan: { addedServings: Number(meal.servings) },
								  } as Stamp);
					  });

				const mergedMealPlanningData: {
					preferences: MealPlanPreference[];
					selectedMeals: Stamp[] | (Stamp | SelectedMealLocalStorage)[];
				} = {
					preferences: partialLocalMealPlanningData.preferences.length
						? partialLocalMealPlanningData.preferences
						: serverMealPlanningData.preferences,
					selectedMeals: partialLocalMealPlanningData.selectedMeals.length
						? mergedLocalSelectedMeals
						: serverMealPlanningData.selectedMeals,
				};

				const hasDifferentPreferencesLocally =
					!!partialLocalMealPlanningData.preferences.length &&
					JSON.stringify(partialLocalMealPlanningData.preferences) !==
						JSON.stringify(serverMealPlanningData.preferences);

				const toSynchronise = [];

				/** If there are preferences stored locally, update the preferences on the server if different */
				if (hasDifferentPreferencesLocally) {
					toSynchronise.push(this.patchPreferences(this.mapToMealPlanPreferences(mergedMealPlanningData.preferences)));
				}

				/** If there are shortlisted meals stored locally, then store/overwrite shortlisted meals on the server */
				if (hasDifferentSelectedMealsLocally && partialLocalMealPlanningData?.selectedMeals) {
					toSynchronise.push(
						this.replaceMealsShortlist(
							partialLocalMealPlanningData.selectedMeals.map(({ id, servings }) => ({ recipeId: id, servings }))
						)
					);
				}

				return !hasDifferentPreferencesLocally && !hasDifferentSelectedMealsLocally
					? of(mergedMealPlanningData)
					: forkJoin(toSynchronise).pipe(map(() => mergedMealPlanningData));
			})
		);
	}

	/**
	 * Fetch all the recipe stamps for the shortlisted meals loaded from local storage
	 * @param mealPlanningData
	 * @private
	 */
	private fetchMissingRecipeStamps(
		partialLocalMealPlanningData:
			| MealPlanningDataLocalStorage
			| {
					preferences: MealPlanPreference[];
					selectedMeals: Stamp[] | (Stamp | SelectedMealLocalStorage)[];
			  }
	): Observable<MealPlanningData> {
		const missingRecipeSummaryList = partialLocalMealPlanningData.selectedMeals.filter((meal) => !meal.recipe);
		const mealsWithRecipeSummary = partialLocalMealPlanningData.selectedMeals.filter(
			(meal) => !!meal.recipe
		) as Stamp[];
		const selectedMealStamps$ =
			missingRecipeSummaryList.length < 1
				? of(mealsWithRecipeSummary)
				: this.recipeService
						.getRecipeSummaryList(
							missingRecipeSummaryList.map(
								(meal) => (meal as SelectedMealLocalStorage).id || (meal as Stamp)?.recipe.id
							)
						)
						.pipe(
							map((recipeList): Stamp[] =>
								recipeList.map(
									(recipe): Stamp => ({
										recipe,
										mealPlan: {
											addedServings: Number(
												(
													partialLocalMealPlanningData.selectedMeals.find(
														(selectedMeal) =>
															(selectedMeal as SelectedMealLocalStorage).id === recipe.id ||
															(selectedMeal as Stamp)?.recipe?.id === recipe.id
													) as SelectedMealLocalStorage
												)?.servings
											),
										},
									})
								)
							),
							map((newMealsWithRecipeSummary): Stamp[] => [...newMealsWithRecipeSummary, ...mealsWithRecipeSummary])
						);

		return selectedMealStamps$.pipe(
			map((selectedMeals) => ({
				preferences: partialLocalMealPlanningData.preferences,
				selectedMeals,
			}))
		);
	}

	private mapServerPreferencesToLocal(mealPlanPreferences: MealPlanPreferences): MealPlanPreference[] {
		const peoplePreference: MealPlanPreference[] = mealPlanPreferences?.household
			? [
					{
						name: MealPlanPreferenceName.people,
						value: {
							adult: mealPlanPreferences?.household?.adults || 0,
							child: mealPlanPreferences?.household?.children || 0,
						},
					},
			  ]
			: [];

		const dietaryNeedsPreference: MealPlanPreference[] = mealPlanPreferences?.mealPlanTypes
			? [
					{
						name: MealPlanPreferenceName.dietaryNeeds,
						value: mealPlanPreferences?.mealPlanTypes.map((type) => String(type)) || [],
					},
			  ]
			: [];

		return [...peoplePreference, ...dietaryNeedsPreference];
	}

	/**
	 * Fetch the logged in user's meal plan preferences
	 */
	private fetchPreferences(): Observable<MealPlanPreferences> {
		return this.apiService.get(`${this.appSettingsService.getEndpoint('mealPlanPreferences')}`);
	}

	/**
	 * Fetched the logged in user's shortlisted meals from the server
	 */
	private fetchMealsShortlist(): Observable<MealShortlist[]> {
		return this.apiService.get(`${this.appSettingsService.getEndpoint('mealPlanShortlist')}`);
	}

	/**
	 * This will replace all the customer's meal shortlist with the meals provided in this list
	 * @param mealsShortlist
	 */
	private replaceMealsShortlist(mealsShortlist: MealShortlistItemModel[]): Observable<void> {
		return this.apiService.post(`${this.appSettingsService.getEndpoint('mealPlanShortlist')}/set`, mealsShortlist);
	}

	private mapToUsableShortlist(shortlist: MealShortlist[]): Stamp[] {
		return shortlist
			.filter((recipe) => !!recipe.recipeStamp && !!recipe.servings)
			.map((recipe) => ({
				recipe: mapToRecipeStampLocal(recipe.recipeStamp as RecipeStamp),
				mealPlan: { addedServings: recipe.servings as number },
			}));
	}

	/**
	 * Update the shortlisted meal details for the logged in user
	 * @param meal
	 */
	private updateShortlistMeal(meal: MealShortlistItemModel): Observable<void> {
		return this.apiService.put(`${this.appSettingsService.getEndpoint('mealPlanShortlist')}`, meal);
	}

	/**
	 * Remove a meal from the logged in user's meals shortlist
	 * @param recipeId
	 */
	private removeShortlistMeal(recipeId: number): Observable<void> {
		return this.apiService.delete(`${this.appSettingsService.getEndpoint('mealPlanShortlist')}/${recipeId}`);
	}
}
